import Immutable, { List, Map, fromJS, OrderedSet } from "immutable";
import _, { update } from "lodash";
import { nanoid } from "nanoid";
import AppConstants from "../constants/AppConstants";
import BlockContainer from "./BlockContainer";
import NameSlugFormatter from "../utils/NameSlugFormatter";
import BlockConverter from "../utils/BlockConverter";
import { checkAndHandleMaxPageQuantity } from "../utils/AppUtils";
import WP from "../constants/WP";
import ValidationUtils from "../utils/ValidationUtils";
import { ensureArray } from "../utils/JSUtils";
import { countItemsBeforeTarget } from "@/utils/DnDUtils";
import { RECENT_PEXELS_PHOTOS_KEY } from "../constants/DesignConstants.mjs";

const {
  CANVAS_SET_BLOCK_CONTENT_SEPARATOR,
  CANVAS_SET_CHILDREN,
  CANVAS_SET_DATA,
  CANVAS_SET_DEFAULT,
  ID_LENGTH,
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_CANVAS_SET,
  ITEM_TYPE_SITE_ITEMS,
  ITEM_TYPE_PAGE_FOLDER,
  ITEM_TYPE_PAGE,
  SITE_ITEM_CANVAS_SET,
} = AppConstants;

const { isDefined } = ValidationUtils;

/** @type {"blocks"} */
const BLOCKS_LIST = "blocks";

/** @type {"pages"} */
const PAGES_LIST = "pages";

/** @type {"canvasSets"} */
const CANVAS_SET_LIST = "canvasSets";

/** @type {"folder"} */
const FOLDER = "folder";

/** @type {"folders"} */
const FOLDERS = "folders";

/** @type {"pId"} */
const PARENT_ID = "pId";

/**
 * @typedef {Object} Folder
 * @prop {string} id
 * @prop {number} index - index within the pages list. Allows this to be inserted dynamically
 * @prop {string} name

 * @prop {string} slug
 * @prop {boolean} wp
 */

/**
 * @typedef {Object} SiteItem
 * @prop {Array<string>} canvasSet
 * @prop {string} id
 * @prop {1=} home
 * @prop {string} name
 * @prop {string} pId - parent ID. A folder in this case.
 * @prop {boolean} publishable
 * @prop {string} slug
 * @prop {Object=} wp - wp config
 */

/**
 * @typedef {import("../vo/DesignUpdate").default} DesignUpdate
 * @typedef {import("../vo/CanvasSetUpdate").default} CanvasSetsUpdate
 * @typedef {import("./SitePage").default} SitePage
 */

/**
 * @typedef {object} CanvasSet
 * @prop {string} name
 * @prop {string} slug
 * @prop {string} id
 * @prop {Array<string>} children - array of child site canvas ids
 * @prop {1=} default - set if default
 */

/**
 *
 * @param {import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteItem>} item
 * @returns
 */
const isBlog = (item) => item.get("wp", false) && !(item.get("home") === 1);

/**
 *
 * @param {import("@/types/TypeUtils.mjs").ImmutableMapFrom<Record<string, SiteItem>>} map
 * @returns {List<string>}
 */
const toIDList = (map) => List(map.valueSeq()).map((item) => item.get("id"));

// FOLDER LIST MUST BE ORDERED BY INDEX
const addFoldersToList = (baseList, folderList, baseIndex = 0) =>
  folderList.reduce((result, folder) => {
    const index = folder.get("index");
    return result.splice(index - baseIndex, 0, folder.set("type", FOLDER));
  }, baseList);

class SiteDesign extends BlockContainer {
  constructor(/** Immutable **/ data) {
    super(data);

    const pageItems = this._getPageList();

    if (pageItems.size) {
      const itemList = pageItems;
      const sortedPageItems = toIDList(this._sortComposedPagesList(itemList));
      const folders = this._reindexFolders(sortedPageItems);
      const filteredPageItems = sortedPageItems.filter(
        (id) => !folders.has(id),
      );

      this.data = this.data
        .set(PAGES_LIST, filteredPageItems)
        .set(FOLDERS, folders);
    }
  }

  /**
   * Applies each update in the updater to the design
   * @param {DesignUpdate|CanvasSetsUpdate} designUpdate
   */
  applyUpdate(designUpdate) {
    const { updates } = designUpdate;

    const design = this.data;

    const updatedDesign = design.withMutations((mutDesign) => {
      updates.forEach((update) => {
        if (update.action) {
          if (AppConstants.UPDATE_ACTION_SET === update.action) {
            let { value } = update;
            // make sure to convert to Immutable object before setting
            if (_.isObject(update.value)) {
              value = Immutable.fromJS(value);
            }

            mutDesign.setIn(update.key, value);
          } else if (AppConstants.UPDATE_ACTION_DELETE === update.action) {
            mutDesign.deleteIn(update.key);
          } else if (AppConstants.UPDATE_ACTION_MERGE === update.action) {
            mutDesign.mergeIn(update.key, update.value);
          }
        } else {
          // default action handling

          if (_.isObject(update.value)) {
            mutDesign.mergeIn(update.key, update.value);
          } else {
            if (_.isEmpty(update.value) && !_.isNumber(update.value)) {
              mutDesign.deleteIn(update.key);
            } else {
              mutDesign.setIn(update.key, update.value);
            }
          }
        }
      });

      return mutDesign;
    });

    this.data = updatedDesign;
  }

  /**
   *
   * @param {String} blockId
   * @param {SitePage} sitePage
   * @returns siteBlock
   */
  convertToSiteBlock(blockId, sitePage) {
    const block = sitePage.getBlock(blockId);
    let siteBlock = null;
    if (block) {
      const blockJS = block.toJS();

      const result = BlockConverter.toSiteBlock(blockJS);

      siteBlock = this.addBlock(result.site);

      const pageBlock = result.page;
      _.set(
        pageBlock,
        AppConstants.BLOCK_PROP_PARENT_BLOCK_ID,
        siteBlock.get("id"),
      );

      sitePage.overwriteBlock(blockId, pageBlock);
    }

    return siteBlock;
  }

  setAsHomePage(pageId) {
    const page = this.getPageItem(pageId);

    let currentHome = this.getHomePage();
    let items = this.getSiteItems();

    if (page) {
      if (currentHome) {
        items = items.deleteIn([currentHome.get("id"), "home"]);
      }

      items = items.setIn([page.get("id"), "home"], 1);

      this.data = this.data.set(ITEM_TYPE_SITE_ITEMS, items);

      currentHome = this.getHomePage();
    }

    return currentHome;
  }

  setCanvasSetsData(canvasSets) {
    this.data = this.data.set(CANVAS_SET_DATA, canvasSets);
  }

  setCanvasSetsListData(canvasSetsList) {
    this.data = this.data.set(CANVAS_SET_LIST, canvasSetsList);
  }

  /**
   * Updates a single canvas set
   * @param {CanvasSet} canvasSet
   */
  updateCanvasSet(canvasSet) {
    const id = canvasSet.get("id");
    if (id) {
      this.data = this.data.setIn([CANVAS_SET_DATA, id], canvasSet);
    }
  }

  getCanvasSets() {
    return this.data.get(CANVAS_SET_DATA, Map());
  }

  getCanvasSetsList() {
    return this.data.get(CANVAS_SET_LIST, List());
  }

  /**
   * Get canvas set by id
   * @param {object} props
   * @prop {string} props.id - canvasSetId
   *
   * @returns {import("@/types/TypeUtils.mjs").ImmutableMapFrom<CanvasSet>}
   */
  getCanvasSet({ id }) {
    return this.data.getIn([CANVAS_SET_DATA, id], Map());
  }

  /**
   * Get canvas set for a page by id
   * @param {object} props
   * @param {string} props.id - pageId
   * @returns {List<string>}
   */
  getPageCanvasSetList({ id }) {
    return this.data.getIn(
      [ITEM_TYPE_SITE_ITEMS, id, SITE_ITEM_CANVAS_SET],
      List(),
    );
  }

  /**
   * Sets default to 1 on a canvas set and removes it from the previous canvasSet (if set)
   * @param {string} canvasSetId
   * @returns
   */
  setAsDefaultCanvasSet(canvasSetId) {
    let canvasSets = this.getCanvasSets();
    const canvasSet = canvasSets.get(canvasSetId);
    const previousDefaultSet = this.getDefaultCanvasSet();

    if (previousDefaultSet) {
      canvasSets = canvasSets.deleteIn([
        previousDefaultSet.get("id"),
        CANVAS_SET_DEFAULT,
      ]);
    }
    canvasSets = canvasSets.setIn([canvasSet.get("id"), CANVAS_SET_DEFAULT], 1);
    this.setCanvasSetsData(canvasSets);

    return this.getDefaultCanvasSet();
  }

  getUniqueId(existingMap) {
    let id = nanoid(ID_LENGTH);
    while (existingMap.has(id)) {
      id = nanoid(ID_LENGTH);
    }
    return id;
  }

  createCanvasSet() {
    const canvasSets = this.getCanvasSets();
    const canvasSetList = this.getCanvasSetsList();
    const nameAndSlug = NameSlugFormatter.format("Canvas Set", canvasSets);
    let canvasSet = fromJS({
      ...nameAndSlug,
      id: this.getUniqueId(canvasSets),
      [CANVAS_SET_CHILDREN]: [CANVAS_SET_BLOCK_CONTENT_SEPARATOR],
    });
    if (canvasSets.size === 0) {
      canvasSet = canvasSet.set(CANVAS_SET_DEFAULT, 1);
    }
    const newId = canvasSet.get("id");

    const newCanvasSets = canvasSets.set(newId, canvasSet);
    this.setCanvasSetsData(newCanvasSets);
    this.setCanvasSetsListData(canvasSetList.push(newId));
    return canvasSet;
  }

  duplicateCanvasSet(canvasSetId) {
    const canvasSets = this.getCanvasSets();
    const canvasSetList = this.getCanvasSetsList();
    const canvasSet = canvasSets.get(canvasSetId);
    const nameAndSlug = NameSlugFormatter.format(
      canvasSet.get("name"),
      canvasSets,
    );
    const newCanvasSet = canvasSet
      .merge(nameAndSlug)
      .set("id", this.getUniqueId(canvasSets))
      .delete(CANVAS_SET_DEFAULT);

    const newId = newCanvasSet.get("id");

    const newCanvasSets = canvasSets.set(newId, newCanvasSet);
    this.setCanvasSetsData(newCanvasSets);
    this.setCanvasSetsListData(canvasSetList.push(newId));
    return newCanvasSet;
  }

  removeCanvasSet({ id }) {
    const canvasSets = this.getCanvasSets();
    const canvasSet = canvasSets.get(id, Map());
    const canvasSetList = this.getCanvasSetsList();
    let wasDefault = false;
    if (canvasSet.get(CANVAS_SET_DEFAULT) === 1 && canvasSets.size > 1) {
      wasDefault = true;
    }

    let newCanvasSets = canvasSets.delete(id);
    if (wasDefault && newCanvasSets.size) {
      const newDefaultId = newCanvasSets.first().get("id");
      newCanvasSets = newCanvasSets.setIn(
        [newDefaultId, CANVAS_SET_DEFAULT],
        1,
      );
    }

    const canvasIndex = canvasSetList.indexOf(id);
    if (canvasIndex !== -1) {
      this.setCanvasSetsData(newCanvasSets);
      this.setCanvasSetsListData(canvasSetList.delete(canvasIndex));

      // remove canvasSet from site items
      const updatedSiteItems = this.getSiteItems().map((item) => {
        const itemCanvasSetList = item.get(SITE_ITEM_CANVAS_SET);
        if (itemCanvasSetList && itemCanvasSetList.size) {
          const index = itemCanvasSetList.indexOf(id);
          if (index !== -1) {
            return item.set(
              SITE_ITEM_CANVAS_SET,
              itemCanvasSetList.delete(index),
            );
          }
        }
        return item;
      });

      this.data = this.data.set(ITEM_TYPE_SITE_ITEMS, updatedSiteItems);
    }

    return canvasIndex;
  }

  moveCanvasSetBlock({ item, target }) {
    const { id: dragItemId, index: dragIndex } = item;
    const { index: dropIndex, canvasSet: id, dropArea } = target;
    const canvasSet = this.getCanvasSet({ id });

    if (canvasSet) {
      const blockList = canvasSet.get(CANVAS_SET_CHILDREN, List());
      const shouldNotMove = dropIndex === dragIndex;

      if (shouldNotMove || !blockList.size) return;

      const initialIndex = blockList.indexOf(dragItemId);
      const insertIndex = dropArea === "bottom" ? dropIndex + 1 : dropIndex;

      // insert at new index, remove at last index if first index doesn't match initial
      const updatedBlockList = blockList.insert(insertIndex, dragItemId);
      const deleteIndex =
        updatedBlockList.indexOf(dragItemId) === initialIndex
          ? initialIndex
          : updatedBlockList.lastIndexOf(dragItemId);
      const trimmedBlockList = updatedBlockList.delete(deleteIndex);

      this.updateCanvasSet(
        canvasSet.set(CANVAS_SET_CHILDREN, trimmedBlockList),
      );
    }
  }

  /**
   * Inserts site canvas Id into canvasSet.children at a particular index.
   * Provided index should be aware of "page_content" separator.
   * @param {string} canvasSetId
   * @param {string} blockId
   * @param {number=} index - not being used at the moment
   * @param {string} area
   */
  insertCanvasIntoSet({ id: canvasSetId, blockId, index = -1, area } = {}) {
    const canvasSet = this.getCanvasSet({ id: canvasSetId });

    if (canvasSet) {
      // refactor this if the do/expressions proposal passes
      const storedBlockList = canvasSet.get(
        CANVAS_SET_CHILDREN,
        fromJS([CANVAS_SET_BLOCK_CONTENT_SEPARATOR]),
      );
      const blockList = storedBlockList.size
        ? storedBlockList
        : fromJS([CANVAS_SET_BLOCK_CONTENT_SEPARATOR]);

      const insertIndex =
        index !== -1
          ? index
          : area === "top"
            ? blockList.indexOf(CANVAS_SET_BLOCK_CONTENT_SEPARATOR)
            : blockList.size;

      if (insertIndex === -1) {
        console.warn(
          `Block ${blockId} in canvas set ${canvasSetId} could not be inserted`,
        );
        return;
      }

      const updatedCanvasSet = canvasSet.set(
        CANVAS_SET_CHILDREN,
        blockList.insert(insertIndex, blockId),
      );
      this.updateCanvasSet(updatedCanvasSet);
    }
  }

  /**
   * Removes site canvas in canvasSet.children at a particular index.
   * Provided index should be aware of "page_content" separator.
   * @param {string} canvasSetId
   * @param {string} blockId
   * @param {number=} index - not being used at the moment
   */
  removeCanvasFromSet({ id: canvasSetId, blockId, index = -1 }) {
    const canvasSet = this.getCanvasSet({ id: canvasSetId });
    if (canvasSet) {
      const blockList = canvasSet.get(CANVAS_SET_CHILDREN);
      const separatorIndex = blockList.indexOf(
        CANVAS_SET_BLOCK_CONTENT_SEPARATOR,
      );

      const deleteIndex =
        index !== -1 && index !== separatorIndex
          ? index
          : isDefined(blockId)
            ? blockList.indexOf(blockId)
            : -1;

      if (deleteIndex === -1) {
        console.warn(
          `Index for block ${blockId} in canvas set ${canvasSetId} not found`,
        );
        return;
      }
      const updatedCanvasSet = canvasSet.set(
        CANVAS_SET_CHILDREN,
        blockList.delete(deleteIndex),
      );
      this.updateCanvasSet(updatedCanvasSet);
    }
  }

  /**
   * Returns the site item map.
   * @returns {Map<string, import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteItem>>}
   */
  _getPageItems() {
    return this.getSiteItems();
  }

  /**
   * @typedef pageConfig
   * @prop {number} insertAt - Index to insert a page at
   * @prop {List=} canvasSet - List of canvasSetIds on a page
   * @prop {string=} pId - Parent id
   */

  /**
   * Adds a page
   * @param  pageData
   * @param  {pageConfig} config
   * @returns pageData
   */
  addPage(pageData, config = {}) {
    checkAndHandleMaxPageQuantity(this._getPageItems().size);
    const { insertAt, canvasSet = List(), pId } = config;

    const currentHome = this.getHomePage();
    const items = this.getSiteItems();

    pageData.pId = this.id;

    const nameAndSlug = NameSlugFormatter.format(pageData.name, items);

    _.merge(pageData, nameAndSlug);

    let isHome = false;
    if (_.isEmpty(currentHome) && !_.has(pageData, "wp")) {
      // if there is no home page and it isn't a WP page
      isHome = true;
    }

    this._addPageItem(
      this._createPageItem(pageData, { isHome, canvasSet, pId }),
      insertAt,
    );

    return pageData;
  }

  /**
   * Adds a page folder at an index
   * @param {{ isWp?: boolean }} data
   * @param {{ insertAt: number }} config - insert index relative to enriched pages list
   */
  addPageFolder(data, config) {
    const { insertAt = 0 } = config;

    const folders = this.getFolders();

    // increment name/slug if exists
    const nameAndSlug = NameSlugFormatter.format(data.name, folders);
    const updatedData = Object.assign({ ...data }, nameAndSlug);
    const folderEntity = this._createPageFolder(updatedData);
    const updatedFolders = folders.set(folderEntity.id, fromJS(folderEntity));

    // add to the top of static/blog list if index isn't provided
    const pages = this._getPageList().splice(insertAt, 0, folderEntity.id);

    const sortedPageItems = toIDList(
      this._sortComposedPagesList(pages, undefined, updatedFolders),
    );
    const reindexedFolders = this._reindexFolders(
      sortedPageItems,
      updatedFolders,
    );
    const filteredPageItems = sortedPageItems.filter(
      (id) => !reindexedFolders.has(id),
    );

    this.data = this.data
      .set(PAGES_LIST, filteredPageItems)
      .set(FOLDERS, reindexedFolders);

    return {
      id: folderEntity.id,
      name: folderEntity.name,
      slug: folderEntity.slug,
    };
  }

  /**
   * Deletes a folder, removes the children from it, and reindexes remaining folders.
   * @param {string} id
   */
  removePageFolder(id) {
    const pageList = this._getPageList();

    // filter and reindex folders
    const filteredList = pageList.delete(pageList.indexOf(id));
    const sortedList = this._sortComposedPagesList(filteredList);
    const reindexedFolders = this._reindexFolders(toIDList(sortedList)).delete(
      id,
    );

    // remove pId from items
    const updatedItems = this.getSiteItems().map((item) =>
      item.get("pId") === id ? item.delete(PARENT_ID) : item,
    );

    this.data = this.data = this.data
      .set(ITEM_TYPE_SITE_ITEMS, updatedItems)
      .set(FOLDERS, reindexedFolders);
  }

  /**
   * Adds a page entity from passed data
   * @param {object} pageData
   * @param {pageConfig} config
   * @returns
   */
  addNewPage(pageData, config) {
    const newPageData = { ...pageData, id: this._generatePageID() };
    return this.addPage(newPageData, config);
  }

  _prepareItemRemoval(itemId) {
    const result = {
      index: -1,
    };

    let pages = this._getPageList();
    let items = this.getSiteItems();
    const index = pages.indexOf(itemId);

    if (index !== -1) {
      const item = items.get(itemId);
      Object.assign(result, {
        index,
        item,
        items,
        pages,
        type: item.get("type", ITEM_TYPE_PAGE),
      });
    }

    return result;
  }

  _handleRemovePageItem({ pages, items, item }) {
    let updatedItems = items;

    // home page is being removed
    const needHomePage = item && item.has("home");

    if (!items.isEmpty() && needHomePage) {
      const newHomePageId = this._determineNewHomeId(pages, items);
      if (newHomePageId) {
        updatedItems = updatedItems.setIn([newHomePageId, "home"], 1);
      }
    }

    return [updatedItems, pages];
  }

  /**
   *
   * @param {string} id
   * @returns
   */
  removeItem(id) {
    const preparedData = this._prepareItemRemoval(id);

    if (preparedData.index !== -1) {
      try {
        let items = preparedData.items.delete(id);
        let pages = preparedData.pages.delete(preparedData.pages.indexOf(id));

        [items, pages] = this._handleRemovePageItem({
          item: preparedData.item,
          items,
          pages,
        });

        const folders = this._reindexFolders(pages);
        const filteredPages = pages.filter((id) => !folders.has(id));
        const previousPages = this.data.get(PAGES_LIST);

        this.data = this.data
          .set(PAGES_LIST, filteredPages)
          .set(ITEM_TYPE_SITE_ITEMS, items)
          .set(FOLDERS, folders);

        return { index: previousPages.indexOf(id) };
      } catch (err) {
        console.error(err);
      }
    }

    return { index: preparedData.index };
  }

  /**
   * Updates theme (but just fonts?)
   * @param {object} themeData
   */
  updateTheme(themeData) {
    const fonts = _.get(themeData, "fonts");
    if (fonts) {
      this._updateThemeFonts(fonts);
    }
  }

  /**
   * Updates site theme with fonts in fontMap
   * @param {object} fontMap
   */
  _updateThemeFonts(fontMap) {
    let { theme } = this;

    _.forEach(fontMap, (value, key) => {
      if (!theme.hasIn(["fonts", key])) {
        theme = theme.setIn(["fonts", key], Immutable.fromJS(value));
      }
    });

    this.data = this.data.set("theme", theme);
  }

  updateFolderName(data) {
    const { name, id } = data;

    const folderItem = this.getFolder(id);

    if (folderItem) {
      const nameAndSlug = NameSlugFormatter.format(
        name,
        this.getFolders(),
        folderItem,
      );
      this.data = this.data.mergeIn([FOLDERS, id], nameAndSlug);
    }
  }

  /**
   * Updates a pages' name and slug
   * @param {string} name
   * @param {SitePage} page
   */
  updatePageName(name, page) {
    const pageItem = this.getPageItem(page.id);
    if (pageItem) {
      const nameAndSlug = NameSlugFormatter.format(
        name,
        this.getSiteItems(),
        page,
      );

      page.data = page.data.merge(nameAndSlug);
      this.data = this.data.mergeIn(
        [ITEM_TYPE_SITE_ITEMS, page.id],
        nameAndSlug,
      );
    }
  }

  updateCanvasSetName({ name, id }) {
    const canvasSets = this.getCanvasSets();
    const canvasSet = canvasSets.get(id);
    if (canvasSet) {
      const nameAndSlug = NameSlugFormatter.format(name, canvasSets, canvasSet);
      this.data = this.data.mergeIn([CANVAS_SET_DATA, id], nameAndSlug);
    }
  }

  /**
   * Updates wp settings in a page and site item for that page
   * @param {object} wordpressTemplate
   * @param {SitePage} page
   */
  updatePageWPTemplate(wordpressTemplate, page) {
    const pageItem = this.getPageItem(page.id);

    if (pageItem) {
      let wp = page.data.get("wp", Immutable.Map());

      wp = wp.set(wordpressTemplate.name, wordpressTemplate.value);

      const template = wp.get("template");
      // remove props from other templates
      if (wp.get("custom") && template !== WP.TEMPLATE_CUSTOM) {
        wp = wp.delete("custom");
      }

      page.data = page.data.merge({ wp });
      this.data = this.data.mergeIn([ITEM_TYPE_SITE_ITEMS, page.id], { wp });
    }
  }

  /**
   * Adds an item to a folder
   * @param {string} id
   * @param {string} folderId
   */
  addToFolder(id, folderId) {
    const updated = this.getPageItem(id).set(PARENT_ID, folderId);
    this.updatePageItem(id, updated);
  }

  removeFromFolder(id) {
    const updated = this.getPageItem(id).delete(PARENT_ID);
    this.updatePageItem(id, updated);
  }

  /**
   * Returns an array of child ids for a folder
   * @param {string} folderId
   * @returns {Array<string>}
   */
  getFolderChildIds(folderId) {
    return folderId
      ? this._getPageItems()
          .valueSeq()
          .filter((item) => item.get(PARENT_ID) === folderId)
          .map((item) => item.get("id"))
          .sort((a, b) => this._getPageIndex(a) - this._getPageIndex(b))
          .toJS()
      : [];
  }

  /**
   * Reindexes folders based on item list.
   * @param {List<string>} itemList
   * @returns
   */
  _reindexFolders(itemList, folders = this.getFolders()) {
    return folders.map((folder) => {
      const index = itemList.indexOf(folder.get("id"));
      return index > -1 ? folder.set("index", index) : folder;
    });
  }

  _sortComposedPagesList(
    pagesList,
    items = this.getSiteItems(),
    folders = this.getFolders(),
  ) {
    const composedList = pagesList.map((id) =>
      items.has(id) ? items.get(id) : folders.get(id),
    );
    const staticItems = composedList.filter((item) => item && !item.get("wp"));
    const blogItems = composedList.filter((item) => item && item.get("wp"));
    return staticItems.concat(blogItems);
  }

  moveItem(item, target = {}) {
    const { id, type, targetIndex, inFolder: itemInFolder } = item;
    const {
      id: dropAreaId,
      index: dropAreaIndex,
      type: dropAreaType,
      inFolder: dropAreaInFolder,
      dropIn,
      bottomDrop: adjustToOuterFolderBottomDrop,
    } = target;

    const listName =
      type === ITEM_TYPE_BLOCK
        ? BLOCKS_LIST
        : type === ITEM_TYPE_CANVAS_SET
          ? CANVAS_SET_LIST
          : PAGES_LIST;

    const isPage = listName === PAGES_LIST;

    const list = isPage ? this._getPageList() : this.data.get(listName);

    let itemOrder = list;

    const index = itemOrder.indexOf(id);

    let moveItems = [id];
    let newTargetIndex = targetIndex;

    // target index needs to be shifted before adding to the bottom (outside) of a folder
    if (adjustToOuterFolderBottomDrop) {
      const childIds = this.getFolderChildIds(dropAreaId) ?? [];
      newTargetIndex += childIds.length;

      // shift drop up to account for a shorter folder child length when coming from within the same folder
      if (this.getPageItemFolder(id) === dropAreaId) {
        newTargetIndex -= 1;
      }
    }

    const isFolderAction =
      isPage &&
      (itemInFolder ||
        dropAreaInFolder ||
        dropAreaType === ITEM_TYPE_PAGE_FOLDER);

    if (type === ITEM_TYPE_PAGE_FOLDER) {
      moveItems = [id, ...this.getFolderChildIds(id)];
    } else if (isFolderAction) {
      const prevFolderId = this.getPageItemFolder(id);
      const folderId =
        (dropAreaInFolder && this.getPageItemFolder(dropAreaId)) ?? dropAreaId;

      const isDropIn =
        dropAreaType === ITEM_TYPE_PAGE_FOLDER && dropIn !== false;
      const isDropOnFolderChild =
        dropAreaInFolder && (!itemInFolder || prevFolderId !== folderId);
      const isRemoval = !dropAreaInFolder && itemInFolder;

      if (isDropIn || isDropOnFolderChild) {
        this.addToFolder(id, folderId);
      } else if (isRemoval) {
        this.removeFromFolder(id);
      }
    }

    // change the ordering of moveItems before we pass to _moveItems
    const reorderedMoveItems = this._reorderMoveItemsByFolder(moveItems);

    const [updatedOrder, updated] = this._moveItems(
      index,
      newTargetIndex,
      list,
      reorderedMoveItems,
    );

    if (updated) {
      if (isPage) {
        const sortedOrder = toIDList(this._sortComposedPagesList(updatedOrder));
        const folders = this._reindexFolders(sortedOrder);
        const filteredOrder = sortedOrder.filter((id) => !folders.has(id));

        this.data = this.data
          .set(listName, filteredOrder)
          .set(FOLDERS, folders);
      } else {
        this.data = this.data.set(listName, updatedOrder);
      }
    }
  }

  /**
   * Reorders moveItems to group folders with their children in proper hierarchy order
   * @param {Array<string>} moveItems - array of item IDs to reorder
   * @returns {Array<string>} reordered array with folders followed by their children
   */
  _reorderMoveItemsByFolder(moveItems) {
    if (!moveItems || moveItems.length <= 1) {
      return moveItems;
    }

    const folders = this.getFolders();
    const reorderedSet = new Set();

    // First pass: add folders and their children in order
    moveItems.forEach((itemId) => {
      if (reorderedSet.has(itemId)) return;

      // Check if this is a folder
      if (folders.has(itemId)) {
        reorderedSet.add(itemId);

        // Add all children of this folder that are also in moveItems
        const childIds = this.getFolderChildIds(itemId);
        childIds.forEach((childId) => {
          if (moveItems.includes(childId) && !reorderedSet.has(itemId)) {
            reorderedSet.add(itemId);
          }
        });
      }
    });

    // Second pass: add any remaining items that aren't folders or folder children
    moveItems.forEach((itemId) => {
      if (!reorderedSet.has(itemId)) {
        reorderedSet.add(itemId);
      }
    });

    return [...reorderedSet];
  }

  /**
   * Moves item to new index. Grabs multiple items in one go if width is greater than one.
   * @param {number} index - current index of item(s) to move
   * @param {number} targetIndex - ending index to move the item(s) to
   * @param {Immutable.List<string>} list - ordered list of item IDs
   * @param {Array<string>|string} items - ID or ordered list of unique IDs to move
   * @param {[Immutable.List<string>, boolean]} items - list of Ids to move. Just for figuring out how many items to move.
   */
  _moveItems(index, targetIndex, list, items) {
    const itemList = ensureArray(items);

    if (itemList.length === 0 || index === -1 || index === targetIndex) {
      return [list, false];
    }

    // Check all items still exist in a list after being used by ensureArray
    for (const id of itemList) {
      if (list.indexOf(id) === -1) {
        return [list, false];
      }
    }

    // Remove all items to move from their current positions
    const itemsToMove = new Set(itemList);
    const remaining = list.filter((id) => !itemsToMove.has(id)).toList();

    // Calculate how many moved items were before the target index
    const movedBeforeTargetCount = countItemsBeforeTarget(
      itemList,
      list,
      targetIndex,
    );

    // Adjust insert index
    let insertIndex = Math.max(0, targetIndex - movedBeforeTargetCount);
    insertIndex = Math.min(insertIndex, remaining.size);

    // Insert items at the calculated position
    const result = remaining.splice(insertIndex, 0, ...itemList);

    return [result, true];
  }

  toggleIsPagePublishable(pageId) {
    const pageItem = this.getPageItem(pageId);

    const updatedPage = !this.isPagePublishable(pageId)
      ? pageItem.delete("noPublish")
      : pageItem.set("noPublish", 1);

    this.data = this.data.setIn([ITEM_TYPE_SITE_ITEMS, pageId], updatedPage);
  }

  isPagePublishable(pageId) {
    const { data = Map() } = this;
    return data.getIn([ITEM_TYPE_SITE_ITEMS, pageId, "noPublish"], 0) !== 1;
  }

  getHomePage() {
    return this.getSiteItems().find((item) => item.has("home"));
  }

  getDefaultCanvasSet() {
    return this.getCanvasSets().find((canvasSet) =>
      canvasSet.has(CANVAS_SET_DEFAULT),
    );
  }

  getCanvasSetByChildBlockAndPage(pageId, siteBlockId) {
    const canvasSets = this.getCanvasSets();
    const pageCanvasSetId = this.getPageItem(pageId).getIn(["canvasSet", 0]);
    const canvasSet = canvasSets.get(pageCanvasSetId);
    if (canvasSet && canvasSet.get("children").includes(siteBlockId)) {
      return canvasSet;
    }
  }

  /**
   * Gets a folder
   * @param {string} folderId
   * @returns {import("@/types/TypeUtils.mjs").ImmutableMapFrom<Folder>}
   */
  getFolder(folderId) {
    return this.data.getIn([FOLDERS, folderId]);
  }

  /**
   * @param {string} pageId
   * @returns {import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteItem>}
   */
  getPageItem(pageId) {
    return this.data.getIn([ITEM_TYPE_SITE_ITEMS, pageId]);
  }

  getPageItemFolder(pageId) {
    return (this.getPageItem(pageId) ?? Map()).get("pId");
  }

  updatePageItem(pageId, data) {
    this.data = this.data.setIn([ITEM_TYPE_SITE_ITEMS, pageId], data);
  }

  getPageItemByIndex(index) {
    const pageId = this.data.getIn([PAGES_LIST, index]);
    return this.getPageItem(pageId);
  }

  _getPageIndex(pageId) {
    return this.data.get(PAGES_LIST).indexOf(pageId);
  }

  getPageIndex(pageId) {
    return this._getPageList().indexOf(pageId);
  }

  getSiteBlockIndex(blockId) {
    return this.data.get(BLOCKS_LIST).indexOf(blockId);
  }

  /**
   * Gets page list. Params include filter for folders.
   * @param {{
   *  withFolders: Boolean
   * }} params
   * @returns {List}
   */
  getPageList({ withFolders = true } = {}) {
    const pageOrder = OrderedSet(this.data.get(PAGES_LIST, List()));
    const items = this.getSiteItems();

    let pages = Immutable.List().asMutable();

    let page = null;
    pageOrder.forEach((val) => {
      page = items.get(val);
      page = page.set("publishable", this.isPagePublishable(page.get("id")));

      if (page) {
        pages.push(page);
      } else {
        console.log("Page Not Found: ", val);
        pages.push(
          Immutable.fromJS(
            this._createPageItem({
              id: val,
              name: `! Not Found - ${val} !`,
              slug: `pagenotfound-${val}`,
            }),
          ),
        );
      }
    });

    pages = pages.asImmutable();

    if (withFolders) {
      const folders = List(this.getFolders().valueSeq()).sort(
        (a, b) => a.get("index") - b.get("index"),
      );

      const staticFolders = folders.filter((entry) => !entry.get("wp"));
      const blogFolders = folders.filter((entry) => !!entry.get("wp"));

      const baseStaticPages = pages.filter((page) => !isBlog(page));
      const baseBlogPages = pages.filter((page) => isBlog(page));

      const staticPagesAndFolders = addFoldersToList(
        baseStaticPages,
        staticFolders,
      );
      const blogPagesAndFolders = addFoldersToList(
        baseBlogPages,
        blogFolders,
        staticPagesAndFolders.size,
      );

      pages = staticPagesAndFolders.concat(blogPagesAndFolders);
    }

    return pages;
  }

  _determineNewHomeId(pages, items) {
    let homeId = null;
    pages.forEach((pageId) => {
      if (items.has(pageId) && !items.hasIn([pageId, "wp"])) {
        // don't choose a page that is a wordpress page
        homeId = pageId;
        return false;
      }
    });

    return homeId;
  }

  /**
   * Inserts page into site items and pages list at the insertIndex
   * @param {object} pageItem
   * @param {number} insertIndex
   */
  _addPageItem(pageItem, insertIndex) {
    let items = this.getSiteItems();
    let pages = this._getPageList();

    items = items.set(pageItem.id, Immutable.Map(pageItem));
    if (_.isInteger(insertIndex)) {
      pages = pages.splice(insertIndex, 0, pageItem.id);
    } else {
      pages = pages.push(pageItem.id);
    }

    try {
      const sortedPageItems = toIDList(
        this._sortComposedPagesList(pages, items),
      );
      const reindexedFolders = this._reindexFolders(sortedPageItems);
      const filteredPageItems = sortedPageItems.filter(
        (id) => !reindexedFolders.has(id),
      );

      this.data = this.data
        .set(PAGES_LIST, filteredPageItems)
        .set(ITEM_TYPE_SITE_ITEMS, items)
        .set(FOLDERS, reindexedFolders);
    } catch (err) {
      console.error(err);
    }
  }

  _createPageFolder(folderData) {
    return {
      id: this._generatePageID(),
      name: folderData.name,
      slug: folderData.slug,
      wp: folderData.isWp,
    };
  }

  _createPageItem(pageData, optionalData = {}) {
    const { isHome, canvasSet, pId } = optionalData;
    const pageItem = {
      id: pageData.id,
      name: pageData.name,
      slug: pageData.slug,
      publishable: this.isPagePublishable(pageData.id),
    };

    if (_.has(pageData, "wp")) {
      pageItem.wp = pageData.wp;
    }

    if (canvasSet) {
      pageItem.canvasSet = canvasSet;
    }

    if (pId) {
      pageItem.pId = pId;
    }

    if (isHome) {
      pageItem.home = 1;
    }

    return pageItem;
  }

  _generatePageID() {
    const items = this.getSiteItems();
    let pageId = null;
    let unique = false;

    while (!unique) {
      pageId = nanoid(ID_LENGTH);
      if (!items.has(pageId)) {
        unique = true;
      }
    }

    return pageId;
  }

  set theme(theme) {
    if (Immutable.Map.isMap(theme)) {
      this.data = this.data.set("theme", theme);
    }
  }

  get theme() {
    return this.data.get("theme");
  }

  get config() {
    return this.data.get("config");
  }

  /**
   * Recently used stock photos, newest first; empty for designs saved before this shipped.
   * @returns {List}
   */
  get recentPexelsPhotos() {
    return this.data.get(RECENT_PEXELS_PHOTOS_KEY, List());
  }

  /**
   *
   * @returns {Map<string, import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteItem>>}
   */
  getSiteItems() {
    return this.data.get(ITEM_TYPE_SITE_ITEMS);
  }

  /**
   *
   * @returns {Map<string, import("@/types/TypeUtils.mjs").ImmutableMapFrom<Folder>>}
   */
  getFolders() {
    return this.data.get(FOLDERS, Map());
  }

  _getPageList() {
    return toIDList(this.getPageList());
  }
}

export default SiteDesign;
